import { TutorialDefinition, TutorialScene } from '../TutorialEngine.js';
import { COLORS } from '../../rendering/Renderer.js';
import { OrbitsVortexTutorialState } from './OrbitsTutorialAdapter.js';

type VState = OrbitsVortexTutorialState;

const PARTIAL = 'EC,D3D4';
const PARTIAL_GATED = `${PARTIAL}|B3=A1+C4`;
const SOLVED = 'EC,D3D4,E1F1,oEF1|B3=A1';

const scenes: TutorialScene[] = [
    // Scene 1: Toggle Gates — animate the gate placement flow
    {
        gameState: { orbits: PARTIAL } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click Activate Vortex\nto place a gate pair', cell: 'D0', anchor: 'center' },
            { type: 'text-label', text: 'Pair must be linked\nin the Vortex Hub', cell: 'C5', anchor: 'center' },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { orbits: PARTIAL },
                { orbits: PARTIAL, gateMode: true },
                { orbits: PARTIAL_GATED, gateMode: true },
                { orbits: PARTIAL_GATED },
            ] as VState[],
        },
    },

    // Scene 2: Activation — toggle an unblocked pair active; a blocked pair stays amber
    {
        gameState: { orbits: 'ECB5C5', gateMode: true, extraGates: [['B5', 'D5']] } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Click a pair\nto activate it', cell: 'B2', anchor: 'center', color: COLORS.teal },
            { type: 'text-label', text: 'Orbit gates block\ngate activation', cell: 'B5', anchor: 'far-above', color: COLORS.amber },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { orbits: 'ECB5C5', gateMode: true, extraGates: [['B5', 'D5']] },
                { orbits: 'ECB5C5|B3=A1', gateMode: true, extraGates: [['B5', 'D5']] },
            ] as VState[],
        },
    },

    // Scene 3: Ship teleportation
    {
        gameState: { orbits: PARTIAL_GATED } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Ship enters\na gate...', cell: 'B5', anchor: 'center' },
            { type: 'text-label', text: '...exits from\nthe other side', cell: 'A1', anchor: 'far-right' },
            { type: 'pulse', cell: 'B3', color: COLORS.purple },
            { type: 'pulse', cell: 'A1', color: COLORS.purple },
        ],
    },

    // Scene 4: Gates can be reused — full solved state
    {
        gameState: { orbits: `${SOLVED}+C4` } as VState,
        renderOptions: {},
        annotations: [
            { type: 'text-label', text: 'Gates can be used\nmultiple times', cell: 'D0', anchor: 'center', color: COLORS.teal },
            { type: 'pulse', cell: 'B3', color: COLORS.purple },
            { type: 'pulse', cell: 'A1', color: COLORS.purple },
        ],
        animate: {
            interval: 1500,
            loop: true,
            frames: [
                { orbits: `${SOLVED}+C4` },
                { orbits: `${SOLVED}+A3` },
            ],
        },
    },
];

/**
 * Vortex Orbits tutorial: 4 scenes on a 6×6 vortex grid with digits 0-3.
 * Teaches ship teleportation through Vortex gates.
 */
export const ORBITS_VORTEX_TUTORIAL: TutorialDefinition = {
    gameType: 'orbits-vortex',
    gridCode: '6/0-3:0C4,1F2,2A3,3D1',
    scenes,
    previewScene: 3,
};
